/** Display helpers for the Tax Payment Dashboard. Every number and date shown comes from the server; these only format it. A missing value is a visible dash. */
export const DASH = '—';

const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export function formatMoney(amount: number | null | undefined): string {
  return amount === null || amount === undefined ? DASH : money.format(amount);
}

/** "2026-04-15" -> "Apr 15, 2026" without going through a Date (no time-zone drift on a calendar date). */
export function formatDay(value: string | null | undefined): string {
  if (!value) return DASH;
  const [y, m, d] = value.slice(0, 10).split('-').map(Number);
  return y && m && d ? `${MONTHS[m - 1]} ${d}, ${y}` : value;
}

/** An instant as "Apr 15, 2026, 2:00 PM UTC". The company time zone is not available in this module (BACKEND GAP), so the instant is shown in UTC and says so. */
export function formatInstant(value: string | null | undefined): string {
  if (!value) return DASH;
  // The API's persisted UTC values come back without a zone suffix (SQL Server datetime2), and a browser reads an offset-free date-time as LOCAL time. Treat one as UTC; keep an explicit Z or offset.
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/i.test(value);
  const d = new Date(/^\d{4}-\d{2}-\d{2}T/.test(value) && !hasOffset ? `${value}Z` : value);
  if (Number.isNaN(d.getTime())) return value;
  const hours = d.getUTCHours();
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  return `${MONTHS[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}, ${hours % 12 === 0 ? 12 : hours % 12}:${minutes} ${hours < 12 ? 'AM' : 'PM'} UTC`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** The reference a person can quote for an outside payment record: the first block of the server's record id (the full id is the tooltip). Never made up here. */
export function recordReference(id: string): string {
  return id.split('-')[0]!.toUpperCase();
}
